/**
 * Utilidad para convertir formato de texto de WhatsApp a HTML
 * Soporta los siguientes formatos de WhatsApp:
 * - *texto* → <strong>texto</strong> (negrita)
 * - _texto_ → <em>texto</em> (cursiva)
 * - ~texto~ → <del>texto</del> (tachado)
 * - ```texto``` → <code class="block">texto</code> (monoespaciado bloque)
 * - `texto` → <code>texto</code> (código inline)
 * - > texto → <blockquote>texto</blockquote> (cita)
 * - * texto o - texto → <ul><li>texto</li></ul> (lista con viñetas)
 * - 1. texto → <ol><li>texto</li></ol> (lista numerada)
 */

export class WhatsAppFormatter {
  
  /**
   * Convierte texto con formato WhatsApp a HTML
   * @param text Texto con formato WhatsApp
   * @returns Texto formateado en HTML
   */
  static formatToHtml(text: string): string {
    if (!text) return '';
    
    let formattedText = text;
    
    // Escapar HTML existente para evitar conflictos
    formattedText = this.escapeExistingHtml(formattedText);
    
    // Aplicar formatos en orden específico para evitar conflictos
    formattedText = this.formatCodeBlocks(formattedText);
    formattedText = this.formatInlineCode(formattedText);
    formattedText = this.formatBold(formattedText);
    formattedText = this.formatStrikethrough(formattedText);
    formattedText = this.formatQuotes(formattedText);
    formattedText = this.formatLists(formattedText);
    
    return formattedText;
  }

  /**
   * Escapa HTML existente para evitar conflictos
   */
  private static escapeExistingHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Formatea texto en negrita: *texto* → <strong>texto</strong>
   */
  private static formatBold(text: string): string {
    // Regex para capturar *texto* pero no dentro de código
    return text.replace(/(?<!`)\*([^*\n]+)\*(?!`)/g, '<strong>$1</strong>');
  }



  /**
   * Formatea texto tachado: ~texto~ → <del>texto</del>
   */
  private static formatStrikethrough(text: string): string {
    return text.replace(/(?<!`)~([^~\n]+)~(?!`)/g, '<del>$1</del>');
  }

  /**
   * Formatea código inline: `texto` → <code>texto</code>
   */
  private static formatInlineCode(text: string): string {
    return text.replace(/`([^`\n]+)`/g, '<code class="inline-code">$1</code>');
  }

  /**
   * Formatea bloques de código: ```texto``` → <code class="block">texto</code>
   */
  private static formatCodeBlocks(text: string): string {
    return text.replace(/```([^`]+)```/g, '<code class="code-block">$1</code>');
  }

  /**
   * Formatea citas: > texto → <blockquote>texto</blockquote>
   */
  private static formatQuotes(text: string): string {
    return text;
  }

  /**
   * Formatea listas con viñetas y numeradas
   */
  private static formatLists(text: string): string {
    const lines = text.split('\n');
    const result: string[] = [];
    let currentList: { type: 'ul' | 'ol' | null; items: string[] } = { type: null, items: [] };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      const bulletMatch = this.getBulletListMatch(line);
      const numberedMatch = this.getNumberedListMatch(line);
      
      if (bulletMatch) {
        currentList = this.handleBulletListItem(currentList, bulletMatch, result);
      } else if (numberedMatch) {
        currentList = this.handleNumberedListItem(currentList, numberedMatch, result);
      } else {
        currentList = this.handleNormalLine(currentList, line, result);
      }
    }

    // Cerrar lista final si existe
    this.closePreviousList(currentList, result);

    return result.join('\n');
  }

  /**
   * Verifica si una línea es un elemento de lista con viñetas
   */
  private static getBulletListMatch(line: string): string | null {
    const match = line.match(/^[\*\-]\s+(.+)/);
    return match ? match[1] : null;
  }

  /**
   * Verifica si una línea es un elemento de lista numerada
   */
  private static getNumberedListMatch(line: string): string | null {
    const match = line.match(/^\d+\.\s+(.+)/);
    return match ? match[1] : null;
  }

  /**
   * Maneja un elemento de lista con viñetas
   */
  private static handleBulletListItem(
    currentList: { type: 'ul' | 'ol' | null; items: string[] },
    itemText: string,
    result: string[]
  ): { type: 'ul' | 'ol' | null; items: string[] } {
    if (currentList.type !== 'ul') {
      this.closePreviousList(currentList, result);
      currentList = { type: 'ul', items: [] };
    }
    currentList.items.push(itemText);
    return currentList;
  }

  /**
   * Maneja un elemento de lista numerada
   */
  private static handleNumberedListItem(
    currentList: { type: 'ul' | 'ol' | null; items: string[] },
    itemText: string,
    result: string[]
  ): { type: 'ul' | 'ol' | null; items: string[] } {
    if (currentList.type !== 'ol') {
      this.closePreviousList(currentList, result);
      currentList = { type: 'ol', items: [] };
    }
    currentList.items.push(itemText);
    return currentList;
  }

  /**
   * Maneja una línea normal (no es elemento de lista)
   */
  private static handleNormalLine(
    currentList: { type: 'ul' | 'ol' | null; items: string[] },
    line: string,
    result: string[]
  ): { type: 'ul' | 'ol' | null; items: string[] } {
    this.closePreviousList(currentList, result);
    result.push(line);
    return { type: null, items: [] };
  }

  /**
   * Cierra la lista actual y la agrega al resultado
   */
  private static closePreviousList(
    currentList: { type: 'ul' | 'ol' | null; items: string[] },
    result: string[]
  ): void {
    if (currentList.type && currentList.items.length > 0) {
      const listItems = currentList.items.map(item => `<li>${item}</li>`).join('');
      result.push(`<${currentList.type} class="list">${listItems}</${currentList.type}>`);
    }
  }

  /**
   * Convierte saltos de línea a <br> tags
   */
  static formatLineBreaks(text: string): string {
    return text.replace(/\n/g, '<br>');
  }

  /**
   * Función principal que aplica todo el formateo
   */
  static format(text: string): string {
    let formatted = this.formatToHtml(text);
    formatted = this.formatLineBreaks(formatted);
    return formatted;
  }
}
